import React from 'react';
import { motion } from 'framer-motion';
import {
  ClaudeLogo,
  OpenAILogo,
  LangChainLogo,
  VectorDbLogo,
  PromptEngineeringLogo,
  FineTuningLogo
} from './AIToolLogos';

interface Technology {
  name: string;
  Icon: React.ComponentType<{ size?: number }>;
  description: string;
  useCases: string[];
  featured?: boolean;
  gradient?: string;
}

const technologies: Technology[] = [
  {
    name: 'Claude (Anthropic)',
    Icon: ClaudeLogo,
    description: 'Extended context windows (200K+), advanced reasoning, and tool use. Perfect for complex agent workflows and long-form tasks.',
    useCases: [
      'Multi-turn agent conversations',
      'Code generation & review',
      'Complex reasoning tasks',
      'Tool use & function calling'
    ],
    featured: true,
    gradient: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)'
  },
  {
    name: 'GPT-4 & GPT-4o',
    Icon: OpenAILogo,
    description: 'Multimodal capabilities, vision, and fast response times. Ideal for real-time applications and visual understanding.',
    useCases: [
      'Vision & image analysis',
      'Real-time chat applications',
      'Structured JSON outputs',
      'Function calling'
    ],
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  },
  {
    name: 'LangChain / LangGraph',
    Icon: LangChainLogo,
    description: 'Build complex agent workflows with state management, conditional branching, and human-in-the-loop patterns.',
    useCases: [
      'Agent orchestration graphs',
      'State management',
      'Conditional workflows',
      'Human-in-the-loop systems'
    ],
    gradient: 'linear-gradient(135deg, #b24bf3 0%, #9333ea 100%)'
  },
  {
    name: 'Vector Databases',
    Icon: VectorDbLogo,
    description: 'Pinecone, Weaviate, Chroma for semantic search and retrieval. Build RAG systems that scale to millions of documents.',
    useCases: [
      'Semantic search',
      'RAG implementations',
      'Document retrieval',
      'Embedding storage'
    ],
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  },
  {
    name: 'Prompt Engineering',
    Icon: PromptEngineeringLogo,
    description: 'Advanced prompting techniques: few-shot, chain-of-thought, ReAct patterns, and meta-prompting for optimal results.',
    useCases: [
      'Zero-shot & few-shot prompting',
      'Chain-of-thought reasoning',
      'ReAct (Reasoning + Acting)',
      'Meta-prompting frameworks'
    ],
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)'
  },
  {
    name: 'Custom Fine-tuning',
    Icon: FineTuningLogo,
    description: 'Domain-specific model fine-tuning on GPT-3.5/GPT-4, LLaMA, Mistral. Optimize for your specific use case and data.',
    useCases: [
      'Domain-specific models',
      'Style & tone adaptation',
      'Task specialization',
      'Cost optimization'
    ],
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
  }
];

const TechExpertise: React.FC = () => {
  return (
    <section id="expertise" style={{
      padding: '8rem 0',
      background: 'linear-gradient(180deg, #000000 0%, #0a0e27 100%)',
      position: 'relative'
    }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Header */}
        <motion.div
          style={{ textAlign: 'center', marginBottom: '5rem' }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p style={{
            color: '#b24bf3',
            fontWeight: '600',
            fontSize: '0.875rem',
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            Technology Stack
          </p>
          <h2 style={{
            fontSize: 'clamp(2.25rem, 4vw, 3rem)',
            fontWeight: 'bold',
            color: '#ffffff',
            marginBottom: '1.5rem',
            lineHeight: '1.2'
          }}>
            Powered by{' '}
            <span style={{
              background: 'linear-gradient(135deg, #00d4ff 0%, #00ffaa 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Cutting-Edge AI
            </span>
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '48rem',
            margin: '0 auto',
            lineHeight: '1.75'
          }}>
            We leverage the most advanced AI models and frameworks to build intelligent systems.
            From Claude's extended context to custom fine-tuned models.
          </p>
        </motion.div>

        {/* Technology Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem'
        }}>
          {technologies.map((tech, index) => (
            <motion.div
              key={tech.name}
              style={{
                background: tech.featured
                  ? 'rgba(0, 212, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.03)',
                padding: '2.5rem',
                borderRadius: '1rem',
                border: tech.featured
                  ? '1px solid rgba(0, 212, 255, 0.2)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden'
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{
                y: -8,
                background: tech.featured
                  ? 'rgba(0, 212, 255, 0.08)'
                  : 'rgba(255, 255, 255, 0.05)',
                border: tech.featured
                  ? '1px solid rgba(0, 212, 255, 0.4)'
                  : '1px solid rgba(178, 75, 243, 0.3)',
                boxShadow: '0 20px 60px -10px rgba(0, 212, 255, 0.3)'
              }}
            >
              {/* Gradient top bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: tech.gradient || 'linear-gradient(135deg, #00d4ff 0%, #b24bf3 100%)'
              }} />

              {/* Logo and Name */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '1.5rem',
                marginTop: '0.5rem'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: tech.gradient || 'linear-gradient(135deg, #00d4ff 0%, #b24bf3 100%)',
                  color: '#ffffff',
                  flexShrink: 0,
                  boxShadow: '0 6px 16px -4px rgba(0, 0, 0, 0.4)'
                }}>
                  <tech.Icon size={26} />
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#ffffff',
                  margin: 0
                }}>
                  {tech.name}
                </h3>
              </div>

              {/* Description */}
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.75',
                marginBottom: '1.5rem'
              }}>
                {tech.description}
              </p>

              {/* Use Cases */}
              <div>
                <p style={{
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: '#00ffaa',
                  marginBottom: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Use Cases
                </p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {tech.useCases.map((useCase, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '0.875rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        paddingLeft: '1.25rem',
                        position: 'relative'
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: '#00d4ff'
                      }}>▸</span>
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechExpertise;
